import type { AnyAction, Middleware } from 'redux';
import { get } from 'lodash';
import { types as authTypes } from '../../actions/backendAuth';
import { types as callTypes } from '../../actions/backendCall';
import { types as asrTypes } from '../../actions/backendAsr';
import { types as mainTypes } from '../../actions/backendMain';
import { types as configTypes } from '../../actions/backendConfig';
import { types as commonTypes } from '../../actions/backendCommon';
import { types as snoozeTypes } from '../../actions/backendSnooze';
import * as uiUserTypes from '../../actions/actionTypes/user';

import { getCallMode, getCallId, getCountDown, shouldNotifyUiAboutReload } from '../../selectors/call';
import { getAsrErrorState, getLatestAsrResults } from '../../selectors/asr';
import {
    getDefaultCallMode,
    getSilencDetectionThreshold,
    getSuggestionCallFrequency,
    shouldSendSystemSoundToAsr
} from '../../selectors/config';
import { isAuthWithPasswordEnabled } from '../../selectors/auth';
import { captureInfoMessage } from '../../../tracker/raven';
import type { RootState } from '../../types';
import * as trainerTypes from '../../actions/actionTypes/trainer';
import * as domainTypes from '../../actions/actionTypes/domain';
import * as suggestionsTypes from '../../actions/actionTypes/suggestions';
import * as salesTypes from '../../actions/actionTypes/sales';
import { CallMode, ERR_INVALID_AUTH_SCHEME } from '../../../constants/backend';

interface BackendStoreApi {
    dispatch: (action: unknown) => unknown;
    getState: () => RootState;
}

const backendEventFanout: Middleware = (store: BackendStoreApi) => {
    // Tracks whether the backend-runtime side ever observed CALL_FEEDING_STARTED
    // for the current call. Only the UI-Only deployment relies on this flag:
    //
    //   - In legacy listener / Electron-with-recording modes the client owns the
    //     audio pipeline locally and the recording indicator is driven by other
    //     state. This middleware (the in-process "backend runtime" that fans
    //     events out to the renderer) is mostly inert there.
    //   - In UI-Only mode the renderer receives `call_start` / feeding-start
    //     signals from the real backend over the ASR WS. The flag exists so we
    //     do NOT emit DOMAIN_CALL_ENDED for a call that never reached actual
    //     audio feeding on the backend (e.g. it was cancelled before any audio
    //     was streamed). Without the gate the UI would briefly show an "ended"
    //     state for a call that effectively never happened.
    //
    // Side-effect of the gate: if the backend somehow emits CALL_ENDED without
    // a prior CALL_FEEDING_STARTED (lost message, race, dropped event), the
    // recording indicator stays "on" forever. That is the exact symptom users
    // report, and it is why we log the else-branch below.
    let isCallFeedingStarted = false;

    return next => (action: AnyAction) => {
        const result = next(action);
        const state = store.getState();
        const dispatch2frontend = store.dispatch;

        if (!dispatch2frontend) {
            return result;
        }

        const { type, payload = {} } = action;

        switch (type) {
            /*
            AUTH EVENTS
             */
            case authTypes.AUTH_FAIL: {
                switch (payload.message) {
                    case ERR_INVALID_AUTH_SCHEME:
                        break;
                    default: {
                        if (isAuthWithPasswordEnabled(state)) {
                            dispatch2frontend({
                                type: uiUserTypes.DO_LOGIN_FAILED,
                                payload
                            });
                        }
                    }
                }
                break;
            }

            /*
            COMMON EVENTS
             */
            case commonTypes.MIC_DEVICE_NOT_AVAILABLE: {
                dispatch2frontend({
                    type: trainerTypes.MIC_DEVICE_NOT_AVAILABLE,
                    payload: { ...payload }
                });
                break;
            }
            case commonTypes.SHOW_NOTIFICATION: {
                dispatch2frontend({
                    type: trainerTypes.SHOW_NOTIFICATION,
                    payload: { ...payload }
                });
                break;
            }
            case commonTypes.RELOAD: {
                dispatch2frontend({
                    type: trainerTypes.RELOAD,
                    payload: { ...payload }
                });
                break;
            }
            case commonTypes.UPDATE_RELOAD_COUNT_DOWN: {
                if (shouldNotifyUiAboutReload(state)) {
                    dispatch2frontend({
                        type: trainerTypes.UPDATE_RELOAD_COUNT_DOWN,
                        payload: {
                            countdown: getCountDown(state)
                        }
                    });
                }
                break;
            }

            case mainTypes.DAEMON_VERSION_RECEIVED:
                dispatch2frontend({
                    type: domainTypes.DOMAIN_DAEMON_VERSION,
                    payload: {
                        daemonVersion: payload.version
                    }
                });
                break;

            case snoozeTypes.SNOOZE_COUNTDOWN:
            case snoozeTypes.SET_SNOOZE_TIMER_SUCCESS: {
                dispatch2frontend({
                    type: trainerTypes.SET_SNOOZE_TIMER_SUCCESS,
                    payload
                });
                break;
            }
            case snoozeTypes.SET_SNOOZE_TIMER_FAIL: {
                dispatch2frontend({
                    type: trainerTypes.SET_SNOOZE_TIMER_FAIL,
                    payload: {
                        error: action.error
                    }
                });
                break;
            }

            /*
            SETTINGS EVENTS
             */

            case configTypes.GET_APP_CONFIG_SUCCESS: {
                const { config } = payload;
                const { integrationMethod, explicitIntegrationSource } = get(config, 'callDetection', {});
                dispatch2frontend({
                    type: trainerTypes.SET_USER_INTERFACE_CONFIG,
                    payload: {
                        enableTrainer: !config.disableTrainer,
                        enableSales: config.enableSales || false,
                        enableEvaluations: config.enableEvaluations || false,
                        enableCallSimulator: config.enableCallSimulator || false,
                        userInterfaceConfig: {
                            ...config.userInterface,
                            displayAssistantChat: Boolean(config.assistantChat?.enabled),
                            assistantChatProjectId: String(config.assistantChat?.projectId || ''),
                            integrationMethod,
                            explicitIntegrationSource,
                            lowCallVolumeReminder: {
                                enabled: Boolean(config.lowCallVolumeReminder?.enabled),
                                thresholdCalls: config.lowCallVolumeReminder?.thresholdCalls,
                                periodHours: config.lowCallVolumeReminder?.periodHours
                            }
                        },
                        bonusScore: state.backend.config.bonusScore,
                        silenceDetectionThreshold: getSilencDetectionThreshold(state),
                        suggestionCallFrequency: getSuggestionCallFrequency(state)
                    }
                });
                break;
            }

            /*
            ASR EVENTS
             */
            case asrTypes.RECOGNITION_READY: {
                dispatch2frontend({
                    type: trainerTypes.SET_MANUAL_BUTTON_AVAILABILITY,
                    payload: {
                        isAvailable: true
                    }
                });
                break;
            }
            case commonTypes.ALL_SETTINGS_RECEIVED: {
                dispatch2frontend({
                    type: domainTypes.DOMAIN_INITIAL_CALL_DATA,
                    payload: getLatestAsrResults(state)
                });

                dispatch2frontend({
                    type: domainTypes.DOMAIN_ASR_ERROR_DATA,
                    payload: getAsrErrorState(state)
                });
                break;
            }
            case asrTypes.UPDATE_USER_ASR_CONFIG_SUCCESS: {
                dispatch2frontend({
                    type: domainTypes.DOMAIN_ASR_ERROR_DATA,
                    payload: getAsrErrorState(state)
                });
                break;
            }

            /*
            CALL EVENTS
             */
            case callTypes.CALL_TIMER_UPDATED:
                dispatch2frontend({
                    type: domainTypes.DOMAIN_CALL_DATA,
                    payload: getLatestAsrResults(state)
                });
                break;
            case callTypes.CALL_HOLDED: {
                dispatch2frontend({ type: trainerTypes.CALL_HOLD });
                break;
            }
            case callTypes.CALL_UNHOLDED: {
                dispatch2frontend({ type: trainerTypes.CALL_UNHOLD });
                break;
            }
            case callTypes.CALL_FEEDING_STARTED: {
                isCallFeedingStarted = true;
                break;
            }
            case callTypes.CALL_STARTED: {
                dispatch2frontend({
                    type: domainTypes.DOMAIN_CALL_STARTED,
                    payload: {
                        systemSoundRecognition: shouldSendSystemSoundToAsr(state),
                        callId: getCallId(state)
                    }
                });
                break;
            }
            case callTypes.CALL_ENDED: {
                if (isCallFeedingStarted) {
                    isCallFeedingStarted = false;
                    dispatch2frontend({
                        type: domainTypes.DOMAIN_CALL_ENDED,
                        payload: getLatestAsrResults(state)
                    });
                } else {
                    // CALL_ENDED arrived without a prior CALL_FEEDING_STARTED. In UI-Only mode
                    // this means DOMAIN_CALL_ENDED is NOT forwarded to the renderer, so the
                    // recording indicator that was previously turned on by DOMAIN_CALL_STARTED
                    // will stay on indefinitely — this is the exact "recording never stops"
                    // symptom users report.
                    //
                    // Two possible causes:
                    //   1. By design — the call was cancelled on the backend before audio
                    //      feeding ever started. In that case DOMAIN_CALL_STARTED should also
                    //      not have been emitted, and the UI is fine.
                    //   2. A bug — feeding-start was emitted on the backend but never reached
                    //      this middleware (lost message, race, dropped event). The UI is stuck.
                    //
                    // We log every occurrence so we can correlate with the raw internal-event
                    // stream and tell which case actually happens in production.
                    captureInfoMessage('call ended without prior feeding start', {
                        type: 'callEndedNoFeedingStart',
                        callId: getCallId(state)
                    });
                }
                break;
            }
            case configTypes.SET_RECORD_CALL: {
                dispatch2frontend({
                    type: trainerTypes.SET_RECORD_CALL,
                    payload: {
                        recordCall: payload.recordCall
                    }
                });
                break;
            }
            case callTypes.RESET_CALL_MODE: {
                dispatch2frontend({
                    type: trainerTypes.OPT_RESET
                });
                break;
            }
            case callTypes.CALL_SET_MODE: {
                const resolvedMode = typeof payload.resolvedMode === 'number' ? payload.resolvedMode : null;
                const mode = payload.mode === CallMode.DEFAULT ? (resolvedMode ?? getDefaultCallMode(state)) : payload.mode;

                const currentMode = getCallMode(state);

                switch (mode) {
                    case CallMode.BOTH:
                    case CallMode.CUSTOMER: {
                        dispatch2frontend({
                            type: trainerTypes.EVENT_OPT_IN
                        });
                        break;
                    }
                    case CallMode.AGENT:
                        if (currentMode === CallMode.BOTH || currentMode === CallMode.CUSTOMER) {
                            if (payload.forgetAudioAndTranscript) {
                                dispatch2frontend({
                                    type: trainerTypes.EVENT_OPT_OUT
                                });
                            } else {
                                dispatch2frontend({
                                    type: trainerTypes.EVENT_OPT_STOP
                                });
                            }
                        }
                        break;
                    case CallMode.NONE: {
                        dispatch2frontend({
                            type: trainerTypes.EVENT_OPT_STOP
                        });
                        break;
                    }
                    default:
                }

                break;
            }
            case callTypes.CALL_NOTIFICATION: {
                dispatch2frontend({
                    type: trainerTypes.CALL_NOTIFICATION,
                    payload // {eventName: 'call_start_phrase'|'call_end_phrase'|...}
                });
                break;
            }

            /*
            OTHER
             */
            case callTypes.GET_NEW_SUGGESTIONS_SUCCESS: {
                dispatch2frontend({
                    type: suggestionsTypes.NEW_SUGGESTIONS,
                    payload
                });
                break;
            }
            case commonTypes.SALES_TRACKING_STATUS: {
                dispatch2frontend({
                    type: salesTypes.SET_SALE_TRACKING_STATUS,
                    payload
                });
                break;
            }
            default:
        }

        return result;
    };
};

export default backendEventFanout;
